/**
 * 成果转让/许可支配权规则（与后端 AchievementTransferUtils 保持一致）
 * 许可/转化/投资可多次；独家转让同证书仅一次
 */

/** 是否为独家转让类型（排除 null/空，避免 Number(null)===0 误判） */
function isExclusiveTransferType(value) {
  return value != null && value !== '' && Number(value) === 0
}

export function canDisposeAchievement(row) {
  if (!row) {
    return false
  }
  const status = Number(row.isAchievement)
  if (status === 3) {
    return false
  }
  if (status === 5) {
    return false
  }
  // 已完成独家转让的原成果不可再操作
  if (status === 2 && isExclusiveTransferType(row.transferType)) {
    return false
  }
  return true
}

const TRANSFER_TYPE_LABELS = {
  0: '独家转让',
  1: '非独家转让',
  2: '成果许可',
  3: '实施转化',
  4: '作价投资'
}

/** 展示转让详情：非独家 / 许可 / 转化 / 作价投资（可多次，每次生成使用权记录） */
const DETAIL_TRANSFER_TYPES = [1, 2, 3, 4]

/** 成果转化类型文案 */
export function formatTransferTypeLabel(value) {
  if (value == null || value === '') {
    return ''
  }
  const type = Number(value)
  return TRANSFER_TYPE_LABELS[type] || String(value)
}

/** 同证书是否已完成独家转让 */
export function hasCertificateExclusiveUsed(row) {
  if (!row) {
    return false
  }
  return row.certificateExclusiveUsed === 1
    || row.certificateExclusiveUsed === true
    || row.certificateExclusiveUsed === '1'
}

/** 非独家/许可/转化/投资可选类型；独家转让同证书仅一次 */
export function getAvailableTransferTypes(transferTypeList, row) {
  if (!transferTypeList) {
    return []
  }
  if (hasCertificateExclusiveUsed(row)) {
    return transferTypeList.filter(item => item.value !== 0)
  }
  return transferTypeList
}

/** 成果转化来源类型（来自转让表 new_achievement_id 关联，或许可/转化衍生记录兜底） */
export function getSourceTransferType(row) {
  if (!row) {
    return null
  }
  if (row.sourceTransferType != null && row.sourceTransferType !== '') {
    return row.sourceTransferType
  }
  if (Number(row.isAchievement) === 5 && row.transferType != null && row.transferType !== '') {
    return row.transferType
  }
  return null
}

function isDerivedFlag(row) {
  return row.derivedFromTransfer === true
    || row.derivedFromTransfer === 1
    || row.derivedFromTransfer === '1'
}

/** 是否为非独家/许可/转化/作价投资审核通过后生成的新使用权记录（原存证不展示详情） */
export function isDerivedLicenseConversionInvestmentRecord(row) {
  if (!row) {
    return false
  }
  const sourceType = row.sourceTransferType != null && row.sourceTransferType !== ''
    ? Number(row.sourceTransferType)
    : null

  if (isDerivedFlag(row) && sourceType != null && DETAIL_TRANSFER_TYPES.includes(sourceType)) {
    return true
  }

  if (sourceType != null && DETAIL_TRANSFER_TYPES.includes(sourceType)) {
    return true
  }

  if (Number(row.isAchievement) === 5) {
    const type = row.transferType != null && row.transferType !== ''
      ? Number(row.transferType)
      : null
    return type != null && DETAIL_TRANSFER_TYPES.includes(type)
  }

  return false
}

/** 独家转让详情：已转让给新持有单位（必须有转让记录中的接收方） */
export function getExclusiveTransferDetail(row) {
  if (!row || isDerivedLicenseConversionInvestmentRecord(row)) {
    return null
  }
  const receiveName = row.exclusiveTransferReceiveName || row.exclusiveReceiveName
  if (receiveName == null || receiveName === '') {
    return null
  }
  return {
    exclusive: true,
    label: `已转让给新持有单位${receiveName}`,
    receiveName,
    time: row.exclusiveTransferTime || '—',
    achievementName: row.achievementName || '—'
  }
}

/** 列表/详情用：转化详情文案（兼容旧调用） */
export function getTransferStatusLabel(row) {
  const exclusive = getExclusiveTransferDetail(row)
  if (exclusive) {
    return exclusive.label
  }
  const detail = getTransferDetail(row)
  if (!detail) {
    return ''
  }
  if (detail.pending) {
    return '转让审核中'
  }
  return detail.transferTypeLabel
}

/** 转让详情：由来源持有单位非独家/许可/转化/投资给接收单位 */
export function getTransferDetail(row) {
  if (!isDerivedLicenseConversionInvestmentRecord(row)) {
    return null
  }
  const type = getSourceTransferType(row)
  if (type == null || !DETAIL_TRANSFER_TYPES.includes(Number(type))) {
    return null
  }
  return buildTransferDetail(row, type, false)
}

function buildTransferDetail(row, type, pending) {
  const transferTypeLabel = formatTransferTypeLabel(type) || '—'
  const sourceHoldingUnit = row.sourceHoldingUnit || row.holdingUnit || '—'
  const targetReceiveName = row.targetReceiveName || row.receiveName || '—'
  return {
    achievementName: row.achievementName || '—',
    time: row.sourceTransferTime || row.transferTime || '—',
    holdingUnit: sourceHoldingUnit,
    sourceHoldingUnit,
    targetReceiveName,
    transferTypeLabel,
    description: `由${sourceHoldingUnit}${transferTypeLabel}给${targetReceiveName}`,
    pending: !!pending
  }
}
